import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Link,
  Typography,
} from "@mui/material";
import { TableCellType } from "components/LazyTable";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import { useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import { IAccreditationChainItem } from "services/KnowfullyAdminClient";

const withAcronym = (name?: string, acronym?: string) =>
  acronym ? `${name} (${acronym})` : (name ?? "-");

const LicensureBodyCell = ({ value }: { value: any }) => {
  const { licensureBodyDetailsUrl } = useGlobalContext();
  const item = value as IAccreditationChainItem;
  const label = withAcronym(item.licensureBodyName, item.licensureBodyAcronym);

  return item.licensureBodyId ? (
    <Link component={RouterLink} to={licensureBodyDetailsUrl(item.licensureBodyId)}>
      {label}
    </Link>
  ) : (
    <>{label}</>
  );
};

const AccreditationBodyCell = ({ value }: { value: any }) => {
  const { qualificationDetailsUrl } = useGlobalContext();
  const item = value as IAccreditationChainItem;
  const label = withAcronym(
    item.accreditationBodyName,
    item.accreditationBodyAcronym,
  );

  return item.accreditationBodyId ? (
    <Link
      component={RouterLink}
      to={qualificationDetailsUrl(item.accreditationBodyId)}
    >
      {label}
    </Link>
  ) : (
    <>{label}</>
  );
};

const SubmissionAuthorityCell = ({ value }: { value: any }) => {
  const { submissionAuthorityDetailsUrl } = useGlobalContext();
  const item = value as IAccreditationChainItem;
  const label = item.submissionAuthorityName ?? "-";

  return item.submissionAuthorityId ? (
    <Link
      component={RouterLink}
      to={submissionAuthorityDetailsUrl(item.submissionAuthorityId)}
    >
      {label}
    </Link>
  ) : (
    <>{label}</>
  );
};

type AccreditationChainProps = {
  items?: IAccreditationChainItem[];
};

const AccreditationChain = ({ items }: AccreditationChainProps) => {
  const { formatMessage: t } = useIntl();

  const rows = (items ?? []).map((item, i) => ({
    ...item,
    id: `${item.licensureBodyId ?? ""}|${item.accreditationBodyId ?? ""}|${i}`,
  }));

  const cells: Array<TableCellType> = [
    {
      accessor: "licensureBodyName",
      align: "left",
      type: "node",
      label: t({ id: "licensureBody" }),
      View: LicensureBodyCell,
    },
    {
      accessor: "accreditationBodyName",
      align: "left",
      type: "node",
      label: t({ id: "accreditationBodyLabel" }),
      View: AccreditationBodyCell,
    },
    {
      accessor: "submissionAuthorityName",
      align: "left",
      type: "node",
      label: t({ id: "submissionAuthority" }),
      View: SubmissionAuthorityCell,
    },
  ];

  return (
    <Accordion sx={{ mt: 3 }} defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t({ id: "accreditationChain" })}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {rows.length === 0 ? (
          <Typography
            component="span"
            sx={{ color: "text.disabled", fontStyle: "italic" }}
          >
            {t({ id: "accreditationChainEmpty" })}
          </Typography>
        ) : (
          <SortableTable
            cells={cells}
            rows={rows}
            dense
            defaultOrderBy="accreditationBodyName"
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AccreditationChain;
